// ============================================================
//  API CONFIG — ADMIN DASHBOARD
//  Path: src/config/api.ts
//
//  CHANGE: added a request timeout (a hung request previously
//  had no limit at all) and network-error tagging so a genuine
//  offline/timeout failure can be shown distinctly from a
//  server-side error.
//
//  FIX (review pass) — 401 handling:
//  - Every 401 used to sign the admin out. But the backend also uses
//    401 for two refusals that are NOT an expired session:
//      • requireRecentAuth → { requiresReauth: true } ("confirm it's
//        you"). The document view link uses a 12-hour window, so after
//        12 hours signed in, opening ANY document logged the admin out.
//      • requireMfa → "requires signing in with your authenticator app".
//    Pages already handle both with their own message, but never got
//    the chance — the redirect happened first. These two now reach the
//    page; any other 401 still ends the session exactly as before.
//  - signOut() wasn't awaited before the redirect, so the page could
//    reload while Firebase still had the (revoked) user saved — the
//    next load's GET /api/auth/me got 401 again → reload → repeat.
//    Sign-out is now awaited, happens once even if several requests
//    fail together, and there's no reload when already on /login
//    (that would wipe the login error message).
//  - A 403 "Account deactivated" (role.middleware — the admin was
//    deactivated mid-session) now also ends the session, instead of
//    leaving a dashboard where every request fails.
//  - A production build without REACT_APP_API_URL silently called
//    http://localhost:3000; it now says so in the console.
// ============================================================

import axios from 'axios'
import { auth } from './firebase'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000'

if (!process.env.REACT_APP_API_URL && process.env.NODE_ENV === 'production') {
  console.error('[api] REACT_APP_API_URL is not set for this build — API requests will go to http://localhost:3000 and fail.')
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000, // 20s — a stalled request shouldn't hang the UI indefinitely
  headers: {
    'Content-Type': 'application/json',
    'x-app-type':   'admin',
  },
})

// ── 401s that are NOT an expired session ─────────────────────
// Exported so pages can tell them apart from each other.

// requireRecentAuth.middleware.js: signed in too long ago for this
// action — the fix is re-verifying, not signing out.
export const isReauthRequired = (err: any): boolean =>
  err?.response?.status === 401 && err?.response?.data?.requiresReauth === true

// requireMfa.middleware.js: this sign-in has no authenticator-app
// factor. That middleware sends no flag, so its message is matched too
// (requiresMfa is honoured if the backend ever adds it).
export const isMfaRequired = (err: any): boolean =>
  err?.response?.status === 401 && (
    err?.response?.data?.requiresMfa === true ||
    /authenticator app/i.test(String(err?.response?.data?.message || ''))
  )

const isDeactivated = (err: any): boolean =>
  err?.response?.status === 403 && err?.response?.data?.message === 'Account deactivated'

api.interceptors.request.use(async (config) => {
  try {
    const user = auth.currentUser
    if (user) {
      const token = await user.getIdToken()
      config.headers.Authorization = `Bearer ${token}`
    }
  } catch {
    // Token refresh failure — request proceeds without auth header;
    // the server will return 401 and the response interceptor below
    // handles sign-out.
  }
  return config
})

// One sign-out at a time, however many requests fail together.
let endingSession = false

const endSession = async () => {
  if (endingSession) return
  endingSession = true
  try {
    await auth.signOut()
  } catch {
    // Nothing more to do — the redirect below still leaves the app.
  }
  if (window.location.pathname !== '/login') {
    window.location.assign('/login')
  } else {
    // Already on the login page: no reload (it would clear the error
    // LoginPage is showing). AuthContext sees the sign-out itself.
    endingSession = false
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      // No response at all — timed out or genuinely offline, not
      // a server-side error. Tagging this distinctly lets calling
      // code show "check your connection" instead of a generic or
      // misleading error message.
      error.isNetworkError = true
    } else if (error.response.status === 401) {
      if (!isReauthRequired(error) && !isMfaRequired(error)) {
        await endSession()
      }
    } else if (isDeactivated(error)) {
      await endSession()
    }
    return Promise.reject(error)
  }
)

export default api